import React from 'react';
import { Theme } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import pickBy from 'lodash-es/pickBy';

import { Grid, Typography } from '@hbf/dsl';
import { FacilitiesValues } from 'ha/models/ListingFacilities/constants';
import { ShowMore } from 'ha/ui.legacy/ShowMore';
import { useMediaQuery } from 'ha/helpers/MediaQuery';

import { ListingFacilities } from '@ha/api/v2/types/Listing';
import { ListingFacilityGroup as ListingFacilityGroupItems } from './ListingFacilityGroupItems';

interface Props {
  groupName: string;
  facilities: ListingFacilities;
}

const useStyles = makeStyles()((theme: Theme) => ({
  facilityGroup: {
    margin: theme.spacing(5, 0),
  },
  container: {
    margin: theme.spacing(2, 0, 0.2, 0),
  },
  title: {
    fontWeight: theme.typography.fontWeightBold,
  },
}));

export const ListingFacilitiesGroup: React.FC<Props> = ({
  groupName,
  facilities,
}) => {
  const { classes } = useStyles();
  const { md } = useMediaQuery();
  const [showMore, setShowMore] = React.useState(true);

  const availableFacilities = pickBy(
    facilities,
    value => value !== FacilitiesValues.No,
  );
  const facilitiesWithNo = pickBy(
    facilities,
    value => value === FacilitiesValues.No,
  );

  const facilityList = Object.entries({
    ...availableFacilities,
    ...facilitiesWithNo,
  });
  const maxNumberOfFacilities = md ? 6 : 4;
  const isLong = facilityList.length > maxNumberOfFacilities;

  const maxFacilitiesToShow = React.useMemo(
    () =>
      isLong && showMore
        ? facilityList.slice(0, maxNumberOfFacilities)
        : facilityList,
    [isLong, showMore, facilityList, maxNumberOfFacilities],
  );

  const facilitiesToDisplay = React.useMemo(
    () =>
      maxFacilitiesToShow.reduce(
        (obj, [facility, index]) => ({ ...obj, [facility]: index }),
        {},
      ),
    [maxFacilitiesToShow],
  );

  const handleShowMoreClick = React.useCallback(() => {
    setShowMore(prevShowMore => !prevShowMore);
  }, []);

  return (
    <Grid
      container
      className={classes.facilityGroup}
      data-test-locator="Listing/ListingFacilitiesGroup"
    >
      <Grid item xs={12}>
        <Typography.H3>{groupName}</Typography.H3>
      </Grid>
      <Grid item xs={12}>
        <Grid container className={classes.container} spacing={2}>
          <ListingFacilityGroupItems facilities={facilitiesToDisplay} />
        </Grid>
        {isLong && (
          <Grid
            item
            xs={12}
            data-test-locator="Listing/ListingFacilitiesGroup/Show more"
          >
            <ShowMore
              showMore={showMore}
              handleClick={handleShowMoreClick}
              dataTestLocator="ShowMore"
            />
          </Grid>
        )}
      </Grid>
    </Grid>
  );
};
